import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { MemberStatusEnum } from '../../../Enum/MemberStatusEnum';
import { MemberLifecycleReportFilters, MemberLifecycleReportRow } from '../../../Models/MemberLifecycle/MemberLifecycle.model';
import {
    fetchMemberLifecycleReport,
    setMemberLifecycleReportFilters,
} from '../../../store/memberLifecycleReport.slice';
import { useAppDispatch } from '../../../store/store';
import { getActiveProviders } from '../../../store/provider.slice';
import { fetchTeams } from '../../../store/careTeams.slice';
import { copyObject } from '../../../utils/common';
import { formatUtcDate } from '../../../utils/timeFormat';
import Authorization from '../../../utils/Authorization';
import { recalculateMemberLifecycle } from '../../../store/memberLifecycle.slice';
import Loading from '../../Shared/Loading';
import {
    buildSelectedLifecycleItem,
    getLifecycleStage,
    getLifecycleSubstage,
    getStageOrdinal,
    lifecycleStages,
    MemberLifecycleSummaryModal,
    SelectedLifecycleItem,
} from '../../MemberChartDetails/MemberLifecycleBar/MemberLifecycleBar';

const totalSubstageCount = lifecycleStages.reduce((count, stage) => count + stage.substages.length, 0);

const memberStatusOptions = [
    { name: 'All', filter: '-1' },
    { name: 'Active & Undetermined', filter: `${MemberStatusEnum.Active},${MemberStatusEnum.Undetermined}` },
    { name: 'Undetermined', filter: MemberStatusEnum.Undetermined.toString() },
    { name: 'Active', filter: MemberStatusEnum.Active.toString() },
    { name: 'Associate', filter: MemberStatusEnum.Associate.toString() },
    { name: 'Scheduled', filter: MemberStatusEnum.Scheduled.toString() },
    { name: 'UncompensatedCare', filter: MemberStatusEnum.UncompensatedCare.toString() },
    { name: 'HRA In Review', filter: MemberStatusEnum.HRAInReview.toString() },
    { name: 'FUHFUM In Progress', filter: MemberStatusEnum.FUHFUMInProgress.toString() },
    { name: 'Lost Insurance Coverage', filter: MemberStatusEnum.LostInsuranceCoverage.toString() },
    { name: 'Preparing For Graduation', filter: MemberStatusEnum.PreparingForGraduation.toString() },
    { name: 'Suspended', filter: MemberStatusEnum.Suspended.toString() },
    { name: 'Incarcerated', filter: MemberStatusEnum.Incarcerated.toString() },
    { name: 'Disenrolled From HRA', filter: MemberStatusEnum.DisenrolledFromHRA.toString() },
    { name: 'Disenrolled From Health Plan', filter: MemberStatusEnum.DisenrolledFromHealthPlan.toString() },
    { name: 'Refused To Continue Services', filter: MemberStatusEnum.RefusedToContinueServices.toString() },
    { name: 'Unreachable', filter: MemberStatusEnum.Unreachable.toString() },
    { name: 'Deceased', filter: MemberStatusEnum.Deceased.toString() },
    { name: 'Disenrolled From CHP', filter: MemberStatusEnum.DisenrolledFromCHP.toString() },
    { name: 'Refused To Enroll', filter: MemberStatusEnum.RefusedToEnroll.toString() },
    { name: 'FUHFUM Close', filter: MemberStatusEnum.FUHFUMClose.toString() },
    { name: 'FUH Closed Billable', filter: MemberStatusEnum.FUHClosedBillable.toString() },
    { name: 'FUH Closed Unbillable', filter: MemberStatusEnum.FUHClosedUnbillable.toString() },
];

const currentSubstageOptions = lifecycleStages.flatMap((stage) =>
    stage.substages.map((substage) => ({
        label: `${stage.label}: ${substage.label}`,
        stageKey: stage.stageKey,
        substageKey: substage.substageKey,
    })),
);

const CompactLifecycleProgressBar = ({
    row,
    onSelect,
}: {
    row: MemberLifecycleReportRow;
    onSelect: (item: SelectedLifecycleItem) => void;
}) => {
    const currentOrdinal = getStageOrdinal(row.StageKey, row.SubstageKey);

    return (
        <Stack spacing={0.75} sx={{ width: '100%', py: 0.75 }}>
            <Box sx={{ display: 'flex', height: 18, width: '100%', overflow: 'hidden', borderRadius: 9, backgroundColor: '#E9EEF3' }}>
                {lifecycleStages.flatMap((stage) =>
                    stage.substages.map((substage) => {
                        const progressItem = row.ProgressItems?.find((item) => item.StageKey === stage.stageKey && item.SubstageKey === substage.substageKey);
                        const substageOrdinal = getStageOrdinal(stage.stageKey, substage.substageKey);
                        const isCompleted = substageOrdinal < currentOrdinal;
                        const isCurrent = substageOrdinal === currentOrdinal;

                        return (
                            <Box
                                key={`${stage.stageKey}-${substage.substageKey}`}
                                title={`${stage.label}: ${substage.label}`}
                                role="button"
                                tabIndex={0}
                                onClick={() =>
                                    onSelect({
                                        ...buildSelectedLifecycleItem(progressItem, stage.stageKey, substage.substageKey),
                                        memberId: row.MemberId,
                                        isCompleted,
                                        isCurrent,
                                    })
                                }
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        onSelect({
                                            ...buildSelectedLifecycleItem(progressItem, stage.stageKey, substage.substageKey),
                                            memberId: row.MemberId,
                                            isCompleted,
                                            isCurrent,
                                        });
                                    }
                                }}
                                sx={{
                                    flex: '1 1 0',
                                    minWidth: 0,
                                    backgroundColor: isCompleted ? stage.color : stage.lightColor,
                                    opacity: isCompleted ? 1 : 0.25,
                                    borderRight: '1px solid rgba(255,255,255,0.7)',
                                    boxShadow: isCurrent ? 'inset 0 0 0 2px #253142, 0 0 8px rgba(37,49,66,0.35)' : undefined,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        opacity: 1,
                                        filter: 'brightness(1.08)',
                                    },
                                    '&:focus-visible': {
                                        outline: '2px solid #253142',
                                        outlineOffset: -2,
                                    },
                                }}
                            />
                        );
                    }),
                )}
            </Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="caption" fontWeight={700} noWrap>
                    {getLifecycleStage(row.StageKey).label}: {getLifecycleSubstage(row.StageKey, row.SubstageKey).label}
                </Typography>
                <Chip
                    size="small"
                    label={`${row.ProgressItems?.filter((item) => item.IsCompleted).length ?? 0}/${totalSubstageCount}`}
                    sx={{ height: 20, fontSize: 11, fontWeight: 700, backgroundColor: '#253142', color: 'common.white' }}
                />
            </Stack>
        </Stack>
    );
};

const MemberLifecycleReport = () => {
    const dispatch = useAppDispatch();
    const { rows, filters, totalCount, isLoading, error } = useSelector((state: RootState) => state.memberLifecycleReport);
    const { isLoading: isRecalculating, isCalculating } = useSelector((state: RootState) => state.memberLifecycle);
    const isLifecycleBusy = isRecalculating || isCalculating;
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const authData = new Authorization();
    const canExecuteMemberLifecycle = authData.isInPermission('Execute Member Lifecycle');
    const [selectedLifecycleItem, setSelectedLifecycleItem] = useState<SelectedLifecycleItem | null>(null);
    const [localFilters, setLocalFilters] = useState<MemberLifecycleReportFilters>(filters);
    const [showInActiveProviders, setShowInActiveProviders] = useState(false);
    const [showInActiveTeams, setShowInActiveTeams] = useState(false);

    const copyProviders = copyObject(activeProviders);
    const filteredProviders = (showInActiveProviders ? copyProviders : copyProviders.filter((provider) => provider.Role.RoleName !== 'Inactive')) || [];
    const filteredTeams = showInActiveTeams ? teams : teams.filter((team) => !team.IsDeleted);

    useEffect(() => {
        dispatch(getActiveProviders(true));
        dispatch(fetchTeams(true));
    }, [dispatch]);

    const buildSearchFilters = useCallback(
        (overrides: Partial<MemberLifecycleReportFilters> = {}): MemberLifecycleReportFilters => ({
            ...localFilters,
            IncludeInActiveProviders: showInActiveProviders,
            IncludeInActiveTeams: showInActiveTeams,
            ...overrides,
        }),
        [localFilters, showInActiveProviders, showInActiveTeams],
    );

    const handleSearch = useCallback(() => {
        const searchFilters = buildSearchFilters({ Page: 0 });
        setLocalFilters(searchFilters);
        dispatch(setMemberLifecycleReportFilters(searchFilters));
        dispatch(fetchMemberLifecycleReport(searchFilters));
    }, [buildSearchFilters, dispatch]);

    const handlePaginationChange = useCallback(
        (page: number, pageSize: number) => {
            const searchFilters = buildSearchFilters({ Page: page, PageSize: pageSize });
            setLocalFilters(searchFilters);
            dispatch(setMemberLifecycleReportFilters(searchFilters));
            dispatch(fetchMemberLifecycleReport(searchFilters));
        },
        [buildSearchFilters, dispatch],
    );

    const handleRerunMemberLifecycle = useCallback(
        async (memberId: string) => {
            if (!memberId || isLifecycleBusy) {
                return;
            }

            await dispatch(recalculateMemberLifecycle(memberId, true));
            dispatch(fetchMemberLifecycleReport(buildSearchFilters()));
        },
        [buildSearchFilters, dispatch, isLifecycleBusy],
    );

    const toggleInactiveProvidersHandler = useCallback((isChecked: boolean) => {
        setShowInActiveProviders(isChecked);
        setLocalFilters((prev) => ({
            ...prev,
            ProviderId: 'All',
            TeamId: 'NotSelected',
        }));
    }, []);

    const toggleInactiveTeamsHandler = useCallback((isChecked: boolean) => {
        setShowInActiveTeams(isChecked);
        setLocalFilters((prev) => ({
            ...prev,
            TeamId: 'All',
            ProviderId: 'NotSelected',
        }));
    }, []);

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'MemberName',
                headerName: 'Member',
                minWidth: 210,
                flex: 1,
                sortable: false,
            },
            {
                field: 'AhcccsId',
                headerName: 'AHCCCS ID',
                minWidth: 120,
                sortable: false,
            },
            {
                field: 'HealthPlan',
                headerName: 'Health Plan',
                minWidth: 180,
                flex: 1,
                sortable: false,
            },
            {
                field: 'Team',
                headerName: 'Team',
                minWidth: 160,
                flex: 1,
                sortable: false,
            },
            {
                field: 'LatestCompletedDate',
                headerName: 'Last Completed Stage Date',
                minWidth: 190,
                sortable: false,
                valueGetter: (value, row) => (row.LatestCompletedDate ? formatUtcDate(row.LatestCompletedDate) : ''),
            },
            {
                field: 'Progress',
                headerName: 'Lifecycle Progress',
                minWidth: 720,
                flex: 4,
                sortable: false,
                renderCell: (params) => (
                    <CompactLifecycleProgressBar row={params.row as MemberLifecycleReportRow} onSelect={setSelectedLifecycleItem} />
                ),
            },
            ...(canExecuteMemberLifecycle
                ? [
                      {
                          field: 'Actions',
                          headerName: 'Actions',
                          minWidth: 80,
                          sortable: false,
                          renderCell: (params) => (
                              <Tooltip title="Re-run member lifecycle">
                                  <span>
                                      <IconButton
                                          size="small"
                                          aria-label="Re-run member lifecycle"
                                          disabled={isLifecycleBusy}
                                          onClick={() => handleRerunMemberLifecycle((params.row as MemberLifecycleReportRow).MemberId)}
                                      >
                                          <AutorenewIcon fontSize="small" />
                                      </IconButton>
                                  </span>
                              </Tooltip>
                          ),
                      } as GridColDef,
                  ]
                : []),
        ],
        [canExecuteMemberLifecycle, handleRerunMemberLifecycle, isLifecycleBusy],
    );

    return (
        <>
            <Loading message="Loading..." />
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Box width="100%" borderBottom={1} paddingBottom={1}>
                        <Typography fontWeight="fontWeightMedium" component="h3">
                            Member Lifecycle Report
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Report showing member progress through CHP lifecycle stages.
                        </Typography>
                    </Box>
                </Grid>

                <Grid size={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }} useFlexGap flexWrap="wrap">
                            <FormControl className="form-control-200">
                                <InputLabel id="member-lifecycle-team-label">Team</InputLabel>
                                <Select
                                    labelId="member-lifecycle-team-label"
                                    value={localFilters.TeamId}
                                    size="small"
                                    label="Team"
                                    renderValue={(selected) => {
                                        if (selected === 'NotSelected') {
                                            return <span style={{ color: 'gray' }}>Not Selected</span>;
                                        }
                                        if (selected === 'All') {
                                            return 'All';
                                        }
                                        const selectedTeam = filteredTeams.find((team) => team.Id === selected);
                                        return selectedTeam ? selectedTeam.Name : '';
                                    }}
                                    onChange={(event) => {
                                        const value = event.target.value as string;
                                        setLocalFilters((prev) => ({
                                            ...prev,
                                            TeamId: value,
                                            ProviderId: value !== 'NotSelected' ? 'NotSelected' : prev.ProviderId,
                                        }));
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                                >
                                    <MenuItem value="NotSelected" disabled sx={{ color: 'gray' }}>
                                        Not Selected
                                    </MenuItem>
                                    <MenuItem value="All">All</MenuItem>
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={<Checkbox checked={showInActiveTeams} onChange={(event) => toggleInactiveTeamsHandler(event.target.checked)} />}
                                        label="Show Inactive"
                                    />
                                    {filteredTeams.map((team) => (
                                        <MenuItem key={team.Id} value={team.Id} sx={{ color: team.IsDeleted ? 'gray' : 'black' }}>
                                            {team.Name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl className="form-control-200">
                                <InputLabel id="member-lifecycle-provider-label">Provider</InputLabel>
                                <Select
                                    labelId="member-lifecycle-provider-label"
                                    value={localFilters.ProviderId}
                                    size="small"
                                    label="Provider"
                                    renderValue={(selected) => {
                                        if (selected === 'NotSelected') {
                                            return <span style={{ color: 'gray' }}>Not Selected</span>;
                                        }
                                        if (selected === 'All') {
                                            return 'All';
                                        }
                                        const provider = filteredProviders.find((item) => item.Id === selected);
                                        return provider ? `${provider.LastName}, ${provider.FirstName}` : '';
                                    }}
                                    onChange={(event) => {
                                        const value = event.target.value as string;
                                        setLocalFilters((prev) => ({
                                            ...prev,
                                            ProviderId: value,
                                            TeamId: value !== 'NotSelected' ? 'NotSelected' : prev.TeamId,
                                        }));
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                                >
                                    <MenuItem value="NotSelected" disabled sx={{ color: 'gray' }}>
                                        Not Selected
                                    </MenuItem>
                                    <MenuItem value="All">All</MenuItem>
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={
                                            <Checkbox checked={showInActiveProviders} onChange={(event) => toggleInactiveProvidersHandler(event.target.checked)} />
                                        }
                                        label="Show Inactive"
                                    />
                                    {[...filteredProviders]
                                        .sort((a, b) => a.LastName.localeCompare(b.LastName))
                                        .map((provider) => (
                                            <MenuItem
                                                key={provider.Id}
                                                value={provider.Id}
                                                sx={{ color: provider.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}
                                            >
                                                {`${provider.LastName}, ${provider.FirstName}`}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>

                            <FormControl sx={{ minWidth: 300 }}>
                                <InputLabel id="member-lifecycle-status-label">Member Status</InputLabel>
                                <Select
                                    labelId="member-lifecycle-status-label"
                                    value={localFilters.MemberStatuses}
                                    size="small"
                                    label="Member Status"
                                    onChange={(event) =>
                                        setLocalFilters((prev) => ({
                                            ...prev,
                                            MemberStatuses: event.target.value as string,
                                        }))
                                    }
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 350, minWidth: 300 } } }}
                                >
                                    {memberStatusOptions.map((status) => (
                                        <MenuItem key={status.filter} value={status.filter}>
                                            {status.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl sx={{ minWidth: 420 }}>
                                <InputLabel id="member-lifecycle-substage-label">Current Lifecycle Substage</InputLabel>
                                <Select
                                    labelId="member-lifecycle-substage-label"
                                    value={
                                        localFilters.IncomingStageKey === 'NotSelected' || localFilters.IncomingStageKey === 'All'
                                            ? localFilters.IncomingStageKey
                                            : `${localFilters.IncomingStageKey}:${localFilters.IncomingSubstageKey}`
                                    }
                                    size="small"
                                    label="Current Lifecycle Substage"
                                    renderValue={(selected) => {
                                        if (selected === 'NotSelected') {
                                            return <span style={{ color: 'gray' }}>Not Selected</span>;
                                        }
                                        if (selected === 'All') {
                                            return 'All';
                                        }
                                        const option = currentSubstageOptions.find((item) => `${item.stageKey}:${item.substageKey}` === selected);
                                        return option?.label ?? selected;
                                    }}
                                    onChange={(event) => {
                                        const value = event.target.value as string;
                                        if (value === 'NotSelected' || value === 'All') {
                                            setLocalFilters((prev) => ({
                                                ...prev,
                                                IncomingStageKey: value,
                                                IncomingSubstageKey: '',
                                            }));
                                            return;
                                        }

                                        const [stageKey, substageKey] = value.split(':');
                                        setLocalFilters((prev) => ({
                                            ...prev,
                                            IncomingStageKey: stageKey,
                                            IncomingSubstageKey: substageKey,
                                        }));
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 350, minWidth: 420 } } }}
                                >
                                    <MenuItem value="NotSelected" disabled sx={{ color: 'gray' }}>
                                        Not Selected
                                    </MenuItem>
                                    <MenuItem value="All">All</MenuItem>
                                    {currentSubstageOptions.map((option) => (
                                        <MenuItem key={`${option.stageKey}:${option.substageKey}`} value={`${option.stageKey}:${option.substageKey}`}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button style={{ minWidth: '120px' }} onClick={handleSearch} variant="contained">
                                Search
                            </Button>

                            <Typography variant="body2" color="text.secondary" sx={{ ml: { lg: 'auto' } }}>
                                {isLoading ? 'Loading lifecycle rows...' : `Showing ${rows.length} of ${totalCount} members`}
                            </Typography>
                        </Stack>
                        {error && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                {error}
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <DataGrid
                        rows={rows}
                        loading={isLoading}
                        columns={columns}
                        autoHeight
                        rowCount={totalCount}
                        paginationMode="server"
                        paginationModel={{ pageSize: filters.PageSize, page: filters.Page }}
                        pageSizeOptions={[20]}
                        onPaginationModelChange={(model) => handlePaginationChange(model.page, model.pageSize)}
                        getRowId={(row) => row.Id}
                        getRowHeight={() => 'auto'}
                        disableRowSelectionOnClick
                        disableColumnSorting
                        sx={{
                            [`& .${gridClasses.cell}`]: {
                                py: 1,
                                alignItems: 'center',
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#F6F8FA',
                                fontWeight: 700,
                            },
                        }}
                    />
                </Grid>
            </Grid>

            <MemberLifecycleSummaryModal selectedLifecycleItem={selectedLifecycleItem} onClose={() => setSelectedLifecycleItem(null)} />
        </>
    );
};

export default MemberLifecycleReport;
